import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatActionIcon as AgentIcon } from '@erp/ui/chat-action-icon';
import { ChatMessage } from '@/components/common/chat';
import { useStickToBottom } from '@/components/common/chat/useStickToBottom';
import {
  FILE_EDITOR,
  SCREENSHOT_STEP,
} from '@/components/common/agentActionFormatting';
import { cn } from '@/lib/utils';
import type { ChatItem } from '../operatorActivityToChat';
import { OperatorTurnCard } from './OperatorTurnCard';
import { OperatorSystemNotice } from './OperatorSystemNotice';
import { OperatorStepMarker } from './OperatorStepMarker';
import { OperatorScreenGroup } from './OperatorScreenGroup';

/** One build-phase section's turns (`header`), or the pre-section intro turns (`header: null`).
 * The chat is segmented on `{kind:'section'}` dividers so each phase collapses independently. */
interface ScreenGroup {
  header: Extract<ChatItem, { kind: 'section' }> | null;
  items: ChatItem[];
}

/** Split the flat item list on section dividers. Items before the first divider are the intro
 * group (always shown); each divider starts a new collapsible group. */
function toScreenGroups(items: ChatItem[]): ScreenGroup[] {
  const groups: ScreenGroup[] = [{ header: null, items: [] }];
  for (const item of items) {
    if (item.kind === 'section') groups.push({ header: item, items: [] });
    else groups[groups.length - 1].items.push(item);
  }
  // Keep the intro group only when it has items; keep every screen group (a screen may have
  // started with no turns yet).
  return groups.filter((g) => g.header !== null || g.items.length > 0);
}

/** Edits + renders in a group, for the collapsed summary. */
function groupCounts(items: ChatItem[]): {
  editCount: number;
  renderCount: number;
} {
  let editCount = 0;
  let renderCount = 0;
  for (const item of items) {
    if (item.kind !== 'turn') continue;
    for (const action of item.actions) {
      if (action.action === FILE_EDITOR) editCount += 1;
      else if (action.action === SCREENSHOT_STEP) renderCount += 1;
    }
  }
  return { editCount, renderCount };
}

interface OperatorChatPanelProps {
  items: ChatItem[];
  isLoading: boolean;
  className?: string;
}

/** The interview's chat body — dispatches each `ChatItem` to the right presentational
 * component (turn card / system notice / step marker / plain bubble) and owns autoscroll.
 * Replaces `ChatMessageList` for this page; that component's streaming/tool-call machinery
 * has nothing to do with operator turns, so this stays a separate, smaller implementation. */
export function OperatorChatPanel({
  items,
  isLoading,
  className,
}: OperatorChatPanelProps) {
  const hasContent = items.length > 0 || isLoading;

  const setContentNode = useStickToBottom();

  const groups = toScreenGroups(items);

  if (!hasContent) {
    return (
      <ScrollArea className={cn('flex-1 min-h-0 px-3', className)}>
        <div className="flex h-full items-center justify-center py-8">
          <p className="text-sm text-muted-foreground">
            <T context="operator interview chat empty state prompt">
              Start teaching the operator to see its progress here.
            </T>
          </p>
        </div>
      </ScrollArea>
    );
  }

  // Mirrors ChatMessageList's isWaitingForFirstToken: only show the spinner right after a
  // human reply, before the agent's next turn has produced anything to render.
  const isWaitingForFirstTurn = isLoading && items.at(-1)?.kind === 'human';

  // Only the newest turn auto-expands its reasoning (same as before, but computed globally so it
  // still holds when turns are nested inside screen groups).
  const lastTurnId =
    [...items].reverse().find((it) => it.kind === 'turn')?.id ?? null;

  const renderItem = (item: ChatItem): ReactNode => {
    switch (item.kind) {
      case 'human':
        return <ChatMessage key={item.id} role="user" content={item.content} />;
      case 'note':
        return (
          <ChatMessage
            key={item.id}
            role="assistant"
            content={item.content}
            badge={item.badge}
          />
        );
      case 'marker':
        return <OperatorStepMarker key={item.id} content={item.content} />;
      case 'system':
        return (
          <OperatorSystemNotice
            key={item.id}
            content={item.content}
            tone={item.tone}
          />
        );
      case 'turn':
        return (
          <OperatorTurnCard
            key={item.id}
            reasoning={item.reasoning}
            actions={item.actions}
            screenshotDataUrl={item.screenshotDataUrl}
            defaultExpanded={item.id === lastTurnId}
          />
        );
      case 'section':
        return null; // rendered as a group header, never inside a group body
    }
  };

  return (
    <ScrollArea className={cn('flex-1 min-h-0 px-3', className)}>
      <div ref={setContentNode} className="space-y-3 py-4">
        {groups.map((group, gi) => {
          const body = group.items.map(renderItem);
          if (!group.header)
            return (
              <div key="intro" className="space-y-3">
                {body}
              </div>
            );
          const { editCount, renderCount } = groupCounts(group.items);
          return (
            <OperatorScreenGroup
              key={group.header.id}
              section={group.header.section}
              editCount={editCount}
              renderCount={renderCount}
              defaultExpanded={gi === groups.length - 1}
            >
              {body}
            </OperatorScreenGroup>
          );
        })}
        {isWaitingForFirstTurn && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 p-2.5" role="status">
              <span className="sr-only">
                <T>Loading</T>
              </span>
              <AgentIcon state="loading" size={24} />
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
